/**
 * Single entry point for reading Supabase runtime configuration.
 *
 * The source of truth is the `window.__ENV__` object, injected by the static
 * script `/config.js` (generated by the container entrypoint from env variables
 * via jq) BEFORE the main bundle loads. This lets one build artifact be reused
 * across environments (demo / staging), switching the Supabase backend without
 * a rebuild.
 *
 * Fallback to `import.meta.env.VITE_*` is needed for:
 *  - local development (`pnpm dev`) without Docker/config.js;
 *  - the transition period while images still bake VITE_* at build time.
 */

declare global {
  interface Window {
    __ENV__?: {
      SUPABASE_URL?: string
      SUPABASE_ANON_KEY?: string
      // Optional display-only channel metadata (dev/stage), injected
      // per-environment — lets one image (dev re-tagged to :staging) report the
      // correct channel without a rebuild.
      APP_CHANNEL?: string
    }
  }
}

/**
 * Read a value from the runtime config with a fallback to build-time env.
 * @param runtimeValue - value from window.__ENV__ (may be undefined/empty)
 * @param buildValue - value from import.meta.env.VITE_* (fallback)
 * @param name - variable name for the error message
 * @returns a non-empty string
 * @throws Error if both sources are empty — the config is required by the client
 */
function resolve(
  runtimeValue: string | undefined,
  buildValue: string | undefined,
  name: string
): string {
  const value = runtimeValue || buildValue
  if (!value) {
    throw new Error(
      `Missing ${name}: neither window.__ENV__ (config.js) nor import.meta.env provides it`
    )
  }
  return value
}

/**
 * Supabase project URL for the current environment.
 * @throws Error if the value is set in neither the runtime nor the build config
 */
export function getSupabaseUrl(): string {
  return resolve(
    window.__ENV__?.SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_URL,
    'SUPABASE_URL'
  )
}

/**
 * Supabase anon (public) key for the current environment.
 * @throws Error if the value is set in neither the runtime nor the build config
 */
export function getSupabaseAnonKey(): string {
  return resolve(
    window.__ENV__?.SUPABASE_ANON_KEY,
    import.meta.env.VITE_SUPABASE_ANON_KEY,
    'SUPABASE_ANON_KEY'
  )
}

/**
 * Runtime deployment channel (e.g. "dev", "stage"), injected per-environment via
 * /config.js. Unlike the Supabase getters it never throws — this is display-only
 * metadata for the version badge.
 * @returns the channel value, or undefined when unset (local dev / a dev image
 *          relying on its build-time fallback)
 */
export function getRuntimeChannel(): string | undefined {
  return window.__ENV__?.APP_CHANNEL || undefined
}
